import React, { useMemo } from 'react';
import classNames from 'classnames';
import {
  ClaimTallyItem,
  ScheduledClaimItemFormatted,
  ContractItemFormatted,
  FiledClaimItemFormatted,
} from './Counterparty.types';
import { formatClaimTallyItem } from './CounterpartyPage.mappers';
import { boldKeys } from './Counterparty.const';

export type TallyTableProps = {
  items: ClaimTallyItem[];
  title: string;
  className?: string;
  skipCount?: boolean;
};

function TallyTable(props: TallyTableProps) {
  const { items: itemsProp, className, title } = props;
  const items = useMemo(() => {
    return itemsProp.map(formatClaimTallyItem);
  }, [itemsProp]);

  const cn = classNames('simple-table', className);
  return (
    <section role="table" className={cn}>
      <h5 className="simple-table-title ">{title}</h5>
      <div className="simple-table-header">Secured</div>
      <div className="simple-table-header">Admin</div>
      <div className="simple-table-header">Priority</div>
      <div className="simple-table-header">Unsecured</div>
      <div className="simple-table-header simple-table-bold">Total</div>

      {items.map((r, index) => {
        return (
          <React.Fragment key={`${r.label}${index}`}>
            <div>{r.label}</div>
            <div>{r.secured_formatted}</div>
            <div>{r.admin_formatted}</div>
            <div>{r.priority_formatted}</div>
            <div>{r.unsecured_formatted}</div>
            <div className="simple-table-bold">{r.total_formatted}</div>
          </React.Fragment>
        );
      })}
    </section>
  );
}

export function FiledClaimsTallyTable(props: {
  items: ClaimTallyItem[];
  title: string;
}) {
  return (
    <TallyTable
      title={props.title}
      items={props.items}
      className="filed-claims-tally-table"
    />
  );
}

export function ScheduledClaimsTallyTable(props: {
  items: ClaimTallyItem[];
  title: string;
}) {
  return (
    <TallyTable
      title={props.title}
      skipCount
      items={props.items}
      className="scheduled-claims-tally-table"
    />
  );
}

type HeaderType = {
  [key:string]: string;
}

type FullTableProps = {
  items: FiledClaimItemFormatted[] | ScheduledClaimItemFormatted[] | ContractItemFormatted[];
  title?: string;
  headers: HeaderType;
};

export type ColumnValueType = string | number | JSX.Element | undefined | boolean;

export const FullTable = (props: FullTableProps) => {
  const { items, title, headers } = props;

  return (
    <div className="counterparty-full-table__container">
      <div className="counterparty-full-table__start-padder"></div>
      <section className="thin-scrollbar counterparty-full-table">
        <h5 className="simple-table-title simple-table-fullwidth">{title}</h5>
          {items.map((item, idx) => (
            <div className="counterparty-full-table__columns">
              {Object.entries(item)?.map((keyVal) => {
                return (
                  <div className="counterparty-full-table__column">
                    <Column
                      title={idx === 0 ? headers[keyVal[0]] : undefined}
                      value={keyVal[1]}
                      itemKey={keyVal[0]}
                    />
                  </div>
                )
              })}
            </div>
          ))}
      </section>
      <div className="counterparty-full-table__end-padder"></div>
    </div>
  );
};

interface ColumnProps {
  title?: string;
  value: ColumnValueType;
  itemKey: string;
}

const Column = ({ title, value, itemKey }: ColumnProps) => {
  const noBoldTitle = !boldKeys.includes(itemKey);
  if (itemKey === 'empty')
    return (
      <div
        className={classNames('counterparty-full-table__column__cell', {
          empty: itemKey === 'empty',
        })}
      ></div>
    );
  return (
    <div className={classNames('counterparty-full-table__column__cell', itemKey)}>
      {title && (
        <div
          className={classNames(
            'counterparty-full-table__column__cell__header',
            {
              'bold-title': noBoldTitle,
            },
            itemKey
          )}
        >
          {title}
        </div>
      )}
      <div className={classNames('counterparty-full-table__column__cell__value', itemKey)}>{value}</div>
    </div>
  );
};